import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorWarningtraiangle } from '../assets/icons';
import {
  ButtonSize,
  ButtonType,
  CheckBox,
  ColorType,
  Label,
  LabelType,
  MODAL_TYPES,
  useGlobalModalContext,
} from '../components/_ui';
import { RoutePath } from '../routes';
import { useLazyFetchCompanyDetailQuery } from '../services/company.api';
import { useLazyFetchPricingModelsQuery } from '../services/pricing.api';
import { BankInfoProvided } from '../stores/types/company.interface';
import { useAuth } from './useAuth';
import { useCompany } from './useCompany';
import { useCompanyPermissions } from './useCompanyPermissions/useCompanyPermissions';
import { useNavigateWithSearchParam } from './useNavigateWithSearchParam';

// Hook to show missing banking info modal when company have missing bank info + have at least one non-free pricing rule
export const useShowBankingInfoModal = () => {
  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();
  const auth = useAuth();
  const { companyId, isMultiViewActive } = useCompany();
  const [showBankInfoModal, setShowBankInfoModal] = useState<boolean>(false);

  const [triggerFetchCompanyDetail, { data: company }] = useLazyFetchCompanyDetailQuery();
  const { userScope } = useCompany();
  const { canViewPriceModels } = useCompanyPermissions(companyId);
  const [triggerFetchingPriceModel, { data: pricing, isFetching: isPricingFetch }] =
    useLazyFetchPricingModelsQuery();
  const { navigateWithCompanyId } = useNavigateWithSearchParam();

  useEffect(() => {
    if (
      companyId &&
      companyId?.toUpperCase() !== 'ALL' &&
      !isMultiViewActive &&
      canViewPriceModels
    ) {
      triggerFetchingPriceModel({ companyId: companyId, scope: userScope! });
    }
  }, [companyId, isMultiViewActive, canViewPriceModels, triggerFetchingPriceModel, userScope]);

  const handleCheckboxChange = useCallback(
    (selected: boolean) => {
      localStorage.setItem(`hideModal-${auth.user.username}-${companyId}`, selected.toString());
    },
    [auth.user.username, companyId],
  );

  const renderModalBody = useCallback(() => {
    return (
      <div className='ml-[36px] mt-2'>
        <Label
          text={t('banking_info_modal_content_1')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
        />
        <Label
          text={t('banking_info_modal_content_2')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          className='pt-2 pb-4'
        />
        <CheckBox label={t('banking_info_modal_checkbox_label')} onChange={handleCheckboxChange} />
      </div>
    );
  }, [t, handleCheckboxChange]);

  const displayBankInfoModal = useCallback(() => {
    showModal(MODAL_TYPES.ALERT_MODAL, {
      height: 'max-content',
      title: t('banking_info_modal_title'),
      icon: ErrorWarningtraiangle,
      iconColor: ColorType.WARNING,
      onRenderBody: renderModalBody,
      buttons: [
        {
          label: t('banking_info_modal_btn_1'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
          dataTestId: 'bankInfoNotNow',
        },
        {
          label: t('banking_info_modal_btn_2'),
          type: ButtonType.PRIMARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
          onClick: () => navigateWithCompanyId(`/${RoutePath.COMPANY}`, companyId),
          dataTestId: 'bankInfoGoToCompany',
        },
      ],
      shouldCloseOnOverlayClick: false,
      testId: 'bankInfoAlertModal',
    });
  }, [showModal, t, renderModalBody, companyId, navigateWithCompanyId]);

  useEffect(() => {
    if (isMultiViewActive || !companyId) {
      return;
    }
    triggerFetchCompanyDetail({ id: companyId, scope: userScope! });
  }, [companyId, isMultiViewActive, triggerFetchCompanyDetail, userScope]);

  useEffect(() => {
    const isBankInfoMissing: boolean = company
      ? company?.bankInfoProvided === BankInfoProvided.MISSING
      : false;

    // nonFreePricing: Indicates if there's at least one non-free pricing rule
    const hasNonFreePricing = pricing
      ? pricing.some((priceObj) => priceObj?.idleRate || priceObj.scheduleType !== 'FREE')
      : false;
    // If user selected "Don't show again checkbox" then hide the modal
    const hideModal =
      localStorage.getItem(`hideModal-${auth.user.username}-${companyId}`) === 'true';
    // Don't show model when price data is being fetched
    // Don't show modal when All companies selected on dropdown
    setShowBankInfoModal(
      isPricingFetch || companyId?.toUpperCase() === 'ALL'
        ? false
        : isBankInfoMissing && hasNonFreePricing && !hideModal,
    );
  }, [company, pricing, isPricingFetch, auth.user.username, companyId]);

  return { showBankInfoModal, displayBankInfoModal };
};
